import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  PasswordInput,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconDatabase, IconRestore } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { ApiError } from "@/lib/api/client";
import {
  confirmRestore,
  createBackup,
  listBackups,
  requestRestore,
} from "@/lib/api/settings";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

export function BackupsPage() {
  const { t } = useTranslation();
  const canCreate = useAuthStore((s) => s.hasPermission("backup.create"));
  const canRestore = useAuthStore((s) => s.hasPermission("backup.restore"));
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["backups"],
    queryFn: listBackups,
  });

  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);
  const [pendingApprovalId, setPendingApprovalId] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");

  const createMutation = useMutation({
    mutationFn: createBackup,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      notifications.show({ color: "blue", message: t("backups.created") });
    },
  });

  const requestMutation = useMutation({
    mutationFn: requestRestore,
    onSuccess: (response) => {
      setPendingApprovalId(response.id);
      openConfirm();
    },
  });

  const confirmMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      confirmRestore(id, password),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      notifications.show({ color: "blue", message: t("backups.restore_confirmed") });
      setPendingApprovalId(null);
      setConfirmPassword("");
      closeConfirm();
    },
  });

  const errorMessage = (() => {
    if (confirmMutation.error instanceof ApiError) return confirmMutation.error.message;
    return null;
  })();

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("backups.title")}
        back
        actions={
          canCreate && (
            <Button
              leftSection={<IconDatabase size={16} strokeWidth={2.2} />}
              radius="xl"
              onClick={() => createMutation.mutate()}
              loading={createMutation.isPending}
            >
              {t("backups.create_now")}
            </Button>
          )
        }
      />

      <Alert color="blue" variant="light" radius="lg">
        {t("backups.scheduled_hint")}
      </Alert>

      <Card padding={0} radius="xl">
        <Table.ScrollContainer minWidth={760}>
          <Table verticalSpacing="md" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("backups.fields.created_at")}</Table.Th>
                <Table.Th>{t("backups.fields.kind")}</Table.Th>
                <Table.Th>{t("backups.fields.size")}</Table.Th>
                <Table.Th>{t("backups.fields.sha256")}</Table.Th>
                <Table.Th>{t("backups.fields.restored_at")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data?.items.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={<IconDatabase size={32} strokeWidth={1.5} />}
                      title={t("backups.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((b) => (
                <Table.Tr key={b.id}>
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {formatDateTime(b.created_at)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" radius="sm">
                      {b.kind}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{b.size_bytes} bytes</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="xs" c="dimmed">
                      {b.sha256.slice(0, 16)}…
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {b.restored_at ? formatDateTime(b.restored_at) : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canRestore && (
                      <Tooltip label={t("backups.request_restore")}>
                        <ActionIcon
                          variant="subtle"
                          color="orange"
                          onClick={() => requestMutation.mutate(b.id)}
                        >
                          <IconRestore size={16} strokeWidth={2} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal
        opened={confirmOpen}
        onClose={closeConfirm}
        title={t("backups.confirm_title")}
        size="md"
      >
        <Stack>
          <Alert color="orange" icon={<IconAlertCircle size={16} />} radius="lg">
            {t("backups.confirm_hint")}
          </Alert>
          {errorMessage && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} radius="lg">
              {errorMessage}
            </Alert>
          )}
          <PasswordInput
            label={t("backups.confirm_password")}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={closeConfirm}>
              {t("common.cancel")}
            </Button>
            <Button
              color="orange"
              radius="xl"
              loading={confirmMutation.isPending}
              onClick={() =>
                pendingApprovalId &&
                confirmMutation.mutate({
                  id: pendingApprovalId,
                  password: confirmPassword,
                })
              }
            >
              {t("backups.confirm_action")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
